import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, Crown, Sparkles, Users, ArrowRight, Gem, Flame, Tag, X } from "lucide-react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const FEATURE_TABLE = {
  free: [
    "Unlimited solo sessions",
    "3 AI meeting minutes per month",
    "7-day recording retention",
    "Goals + carryover + auto-recurring sessions",
  ],
  pro: [
    "Unlimited AI meeting minutes",
    "Unlimited recording retention",
    "Email reminders 15 min before sessions",
    "1 accountability partner with live WebRTC calls",
    "Sign-off / acknowledgements on shared resolutions",
  ],
  syndicate: [
    "Everything in Pro",
    "Up to 10 partners — friend group / mastermind / team",
    "Priority support",
    "Group-board mode (coming soon)",
  ],
};

function Tier({ name, price, period, cta, ctaTestId, features, accent, sublabel, current, onAction, disabled, discountedPrice }) {
  return (
    <div className={`relative surface rounded-md p-8 flex flex-col gap-6 ${accent ? "border-[#c89f45] shadow-[0_30px_80px_-20px_rgba(200,159,69,0.25)]" : ""}`} data-testid={`tier-${name.toLowerCase()}`}>
      {accent && (
        <span className="absolute -top-3 left-8 bg-[#c89f45] text-[#0a0a0a] text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded">
          Most chosen
        </span>
      )}
      {current && (
        <span className="absolute -top-3 right-8 bg-[#1B3B36] text-[#9bd3c4] text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded">
          Current
        </span>
      )}
      <div>
        <p className="label-eyebrow">{sublabel}</p>
        <h3 className="font-serif text-3xl mt-3">{name}</h3>
        <div className="mt-4 flex items-baseline gap-2">
          {discountedPrice != null && discountedPrice !== price ? (
            <>
              <span className="font-serif text-5xl text-[#c89f45]">${discountedPrice}</span>
              <span className="text-sm text-[#888]">/ {period}</span>
              <span className="text-sm text-[#8a8a8a] line-through ml-2">${price}</span>
            </>
          ) : (
            <>
              <span className="font-serif text-5xl">${price}</span>
              <span className="text-sm text-[#888]">/ {period}</span>
            </>
          )}
        </div>
      </div>
      <ul className="space-y-3 flex-1">
        {features.map((f, i) => (
          <li key={`${i}:${f}`} className="flex items-start gap-3 text-sm text-[#ddd]">
            <Check size={14} className="text-[#c89f45] mt-1 shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <Button
        onClick={onAction}
        disabled={disabled}
        className={`rounded-md h-11 ${accent ? "bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a]" : "bg-transparent border border-[#c89f45] text-[#c89f45] hover:bg-[#c89f45] hover:text-[#0a0a0a]"}`}
        data-testid={ctaTestId}
      >
        {cta} <ArrowRight size={14} className="ml-2" />
      </Button>
    </div>
  );
}

function BadgeApplied() {
  return (
    <span className="h-6 w-6 rounded-full bg-[#c89f45] flex items-center justify-center">
      <Check size={12} className="text-[#0a0a0a]" strokeWidth={3} />
    </span>
  );
}

export default function Pricing() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [cycle, setCycle] = useState("monthly");
  const [billing, setBilling] = useState(null);
  const [founding, setFounding] = useState(null);
  const [loading, setLoading] = useState(false);

  // Referral code state
  const [refInput, setRefInput] = useState("");
  const [refValid, setRefValid] = useState(null); // {valid, discount_percent, code, reason}
  const [refOpen, setRefOpen] = useState(false);

  useEffect(() => {
    api.get("/billing/me").then((r) => setBilling(r.data)).catch(() => {});
    api.get("/billing/founding-seats").then((r) => setFounding(r.data)).catch(() => {});
    // Auto-populate referral code from URL (?ref=XXXX)
    const fromUrl = (params.get("ref") || "").toUpperCase().trim();
    if (fromUrl) {
      setRefInput(fromUrl);
      setRefOpen(true);
      // Validate eagerly against pro_monthly (best-effort)
      api.get(`/billing/referrals/validate/${fromUrl}?plan_id=pro_monthly`).then((r) => {
        setRefValid(r.data);
        if (r.data.valid) toast.success(`Referral code applied · ${r.data.discount_percent}% off`);
      }).catch(() => {});
    }
  }, []);

  const currentTier = billing?.effective?.tier || "free";

  const startCheckout = async (planId) => {
    setLoading(true);
    try {
      const body = { plan_id: planId, origin_url: window.location.origin };
      if (refValid?.valid && refValid.applies_to_plan_ids?.includes(planId)) {
        body.referral_code = refValid.code;
      }
      const res = await api.post("/billing/checkout", body);
      window.location.href = res.data.url;
    } catch (e) {
      console.error(e);
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.message || "Could not start checkout");
      setLoading(false);
    }
  };

  const applyReferral = async () => {
    const code = refInput.toUpperCase().trim();
    if (!code) return;
    try {
      const r = await api.get(`/billing/referrals/validate/${code}?plan_id=${proPlanId}`);
      setRefValid(r.data);
      if (r.data.valid) toast.success(`Code accepted · ${r.data.discount_percent}% off Pro & Syndicate`);
      else toast.error(r.data.reason || "Invalid code");
    } catch {
      toast.error("Could not validate code");
    }
  };

  const clearReferral = () => {
    setRefInput("");
    setRefValid(null);
  };

  const startTrial = async () => {
    setLoading(true);
    try {
      await api.post("/billing/trial");
      toast.success("7-day Pro trial started.");
      const r = await api.get("/billing/me");
      setBilling(r.data);
      setLoading(false);
    } catch (e) {
      toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Could not start trial");
      setLoading(false);
    }
  };

  const cancelPlan = async () => {
    if (!window.confirm("Cancel your subscription? You'll keep current-tier access until the period ends, then drop to Free.")) return;
    try {
      await api.post("/billing/cancel");
      const r = await api.get("/billing/me");
      setBilling(r.data);
      toast.success("Subscription cancelled.");
    } catch {
      toast.error("Could not cancel");
    }
  };

  const proAmount = cycle === "monthly" ? 9 : 86;
  const synAmount = cycle === "monthly" ? 29 : 278;
  const proPlanId = cycle === "monthly" ? "pro_monthly" : "pro_yearly";
  const synPlanId = cycle === "monthly" ? "syndicate_monthly" : "syndicate_yearly";

  // Discounted display amount when a referral code is applied AND applies to the plan
  const proDiscounted = refValid?.valid && refValid.applies_to_plan_ids?.includes(proPlanId)
    ? Number((proAmount * (1 - refValid.discount_percent / 100)).toFixed(2))
    : null;
  const synDiscounted = refValid?.valid && refValid.applies_to_plan_ids?.includes(synPlanId)
    ? Number((synAmount * (1 - refValid.discount_percent / 100)).toFixed(2))
    : null;

  return (
    <div className="space-y-14" data-testid="pricing-root">
      <header className="text-center max-w-2xl mx-auto">
        <p className="label-eyebrow">Subscriptions</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-4">
          Sit on the <span className="text-[#c89f45]">right board</span>.
        </h1>
        <p className="text-[#888] mt-6">
          Run your boardroom for free forever. When the practice sticks, upgrade — unlock AI minutes, partners, reminders, and live calls.
        </p>

        {billing?.effective && (
          <p className="font-mono text-xs text-[#8a8a8a] mt-6" data-testid="current-plan-badge">
            CURRENT · <span className="text-[#c89f45] uppercase">{billing.effective.tier}</span>
            {billing.effective.source === "trial" && billing.effective.expires_at && (
              <> · trial ends {new Date(billing.effective.expires_at).toLocaleDateString()}</>
            )}
            {billing.effective.source === "paid" && billing.effective.expires_at && (
              <> · renews {new Date(billing.effective.expires_at).toLocaleDateString()}</>
            )}
          </p>
        )}

        <div className="inline-flex items-center mt-8 p-1 border border-[#2a2a2a] rounded-md bg-[#121212]" data-testid="cycle-toggle">
          {[
            { id: "monthly", label: "Monthly" },
            { id: "yearly", label: "Yearly · save 20%" },
          ].map((c) => (
            <button
              key={c.id}
              onClick={() => setCycle(c.id)}
              className={`px-4 py-2 min-h-[44px] sm:min-h-0 text-sm rounded transition-colors ${
                cycle === c.id ? "bg-[#c89f45] text-[#0a0a0a]" : "text-[#888] hover:text-white"
              }`}
              data-testid={`cycle-${c.id}`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {/* Referral code */}
        <div className="mt-6 flex flex-col items-center gap-2" data-testid="referral-block">
          {!refOpen && !refValid?.valid && (
            <button
              onClick={() => setRefOpen(true)}
              className="text-xs text-[#888] hover:text-[#c89f45] flex items-center gap-1.5 transition-colors"
              data-testid="referral-toggle"
            >
              <Tag size={12} /> Got a referral code?
            </button>
          )}
          {refOpen && !refValid?.valid && (
            <div className="flex items-center gap-2" data-testid="referral-input-row">
              <Input
                value={refInput}
                onChange={(e) => setRefInput(e.target.value.toUpperCase())}
                placeholder="REFCODE8"
                className="w-48 h-10 bg-[#121212] border-[#2a2a2a] focus:border-[#c89f45] font-mono text-center tracking-widest"
                data-testid="referral-input"
                maxLength={16}
              />
              <Button
                onClick={applyReferral}
                disabled={!refInput.trim()}
                className="bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] h-10"
                data-testid="referral-apply"
              >
                Apply
              </Button>
              <button onClick={() => { setRefOpen(false); setRefInput(""); }} className="text-[#8a8a8a] hover:text-white p-1" data-testid="referral-close">
                <X size={14} />
              </button>
            </div>
          )}
          {refValid?.valid && (
            <div className="flex items-center gap-3 px-4 py-2 rounded-md bg-[#c89f45]/10 border border-[#c89f45]/40" data-testid="referral-applied">
              <BadgeApplied />
              <span className="text-sm">
                <span className="font-mono text-[#c89f45]">{refValid.code}</span>
                <span className="text-[#888] ml-2">· {refValid.discount_percent}% off Pro & Syndicate</span>
              </span>
              <button onClick={clearReferral} className="text-[#888] hover:text-white text-xs ml-2" data-testid="referral-remove">
                Remove
              </button>
            </div>
          )}
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
        <Tier
          name="Chair"
          sublabel="Free forever"
          price={0}
          period={cycle === "monthly" ? "mo" : "yr"}
          features={FEATURE_TABLE.free}
          cta={currentTier === "free" ? "You're on Free" : "Continue Free"}
          ctaTestId="cta-free"
          current={currentTier === "free"}
          onAction={() => nav("/")}
          disabled={currentTier === "free"}
        />
        <Tier
          name="Boardroom Pro"
          sublabel={cycle === "monthly" ? "Per month" : "Per year · 20% off"}
          price={proAmount}
          discountedPrice={proDiscounted}
          period={cycle === "monthly" ? "mo" : "yr"}
          features={FEATURE_TABLE.pro}
          cta={billing?.trial_used ? `Get Pro` : `Start 7-day trial`}
          ctaTestId="cta-pro"
          accent
          current={currentTier === "pro"}
          onAction={() => (billing?.trial_used || currentTier !== "free") ? startCheckout(proPlanId) : startTrial()}
          disabled={loading}
        />
        <Tier
          name="Syndicate"
          sublabel={cycle === "monthly" ? "Per month" : "Per year · 20% off"}
          price={synAmount}
          discountedPrice={synDiscounted}
          period={cycle === "monthly" ? "mo" : "yr"}
          features={FEATURE_TABLE.syndicate}
          cta="Get Syndicate"
          ctaTestId="cta-syndicate"
          current={currentTier === "syndicate"}
          onAction={() => startCheckout(synPlanId)}
          disabled={loading}
        />
      </div>

      {/* Founding Members — limited time launch tier */}
      {founding && (
        <div className="max-w-6xl mx-auto" data-testid="founding-card">
          <div className="relative overflow-hidden rounded-md grain-overlay" style={{
            background: "linear-gradient(135deg, #1a1207 0%, #0a0a0a 60%)",
            border: "1px solid #c89f45",
            boxShadow: "0 30px 80px -30px rgba(200,159,69,0.4)",
          }}>
            <div className="absolute -top-px left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#c89f45] to-transparent" />
            <div className="p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-8">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Flame size={14} className="text-[#c89f45]" />
                  <p className="label-eyebrow text-[#c89f45]">Founding Members · limited launch</p>
                </div>
                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-3 tracking-tight">
                  Lifetime Pro for <span className="text-[#c89f45]">$99</span>.
                </h2>
                <p className="text-[#aaa] mt-4 max-w-xl text-sm leading-relaxed">
                  First 100 people only. One-time payment, no renewal, ever — every Pro feature, for as long as Boardroom exists.
                  After the 100th seat is taken, the doors close.
                </p>
                <div className="flex flex-wrap gap-x-6 gap-y-2 mt-5">
                  {[
                    "Unlimited AI minutes",
                    "Unlimited recording",
                    "Email reminders",
                    "1 partner + live calls",
                    "All future Pro features",
                  ].map((f, i) => (
                    <span key={`${i}:${f}`} className="text-xs text-[#ddd] flex items-center gap-2">
                      <Check size={12} className="text-[#c89f45]" /> {f}
                    </span>
                  ))}
                </div>
              </div>
              <div className="md:w-72 shrink-0 flex flex-col items-stretch gap-4">
                <div className="surface rounded-md p-5 text-center border-[#c89f45]/30">
                  <p className="label-eyebrow">Seats remaining</p>
                  <p className="font-serif text-5xl text-[#c89f45] mt-2 tracking-tighter" data-testid="founding-remaining">
                    {founding.remaining}
                    <span className="text-2xl text-[#8a8a8a]"> / {founding.total}</span>
                  </p>
                  <div className="mt-4 h-1 bg-[#1a1a1a] rounded-full overflow-hidden">
                    <div
                      className="h-1 bg-[#c89f45] transition-all duration-700"
                      style={{ width: `${Math.max(2, (founding.taken / founding.total) * 100)}%` }}
                    />
                  </div>
                  <p className="text-[10px] font-mono text-[#8a8a8a] mt-2">{founding.taken} CLAIMED</p>
                </div>
                {founding.sold_out ? (
                  <Button disabled className="bg-[#1a1a1a] text-[#8a8a8a] rounded-md h-12" data-testid="founding-sold-out">
                    All seats claimed
                  </Button>
                ) : (
                  <Button
                    onClick={() => startCheckout("founding_lifetime")}
                    disabled={loading || currentTier !== "free"}
                    className="bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] rounded-md h-12 font-medium"
                    data-testid="cta-founding"
                  >
                    {currentTier !== "free" ? "You're already on a paid plan" : `Claim seat #${founding.taken + 1} · $99 once`}
                    {currentTier === "free" && <ArrowRight size={14} className="ml-2" />}
                  </Button>
                )}
                <button
                  onClick={() => nav("/founders")}
                  className="text-xs text-[#888] hover:text-[#c89f45] mt-1 text-center transition-colors"
                  data-testid="founding-view-wall"
                >
                  View the founders wall →
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {billing && billing.effective && billing.effective.source !== "free" && (
        <div className="max-w-3xl mx-auto surface rounded-md p-6 flex items-center justify-between" data-testid="manage-billing">
          <div>
            <p className="label-eyebrow">Manage</p>
            <p className="text-sm mt-1 text-[#aaa]">
              Stop further charges. You keep current-tier access until {billing.effective.expires_at ? new Date(billing.effective.expires_at).toLocaleDateString() : "end of period"}.
            </p>
          </div>
          <Button onClick={cancelPlan} variant="outline" className="bg-transparent border-[#2a2a2a] text-[#fafafa] hover:bg-[#1a1a1a] hover:text-white" data-testid="cancel-plan-button">
            Cancel subscription
          </Button>
        </div>
      )}

      <div className="text-center text-xs text-[#8a8a8a] font-mono pt-6">
        <p>Powered by Stripe · Test mode · No real charges</p>
      </div>
    </div>
  );
}
